
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";

/**
 * Creates a notification for user actions
 * 
 * @param title Notification title
 * @param message Notification message
 * @param type Notification type (e.g., 'mfo_created', 'department_updated')
 * @param userId User ID to associate the notification with (defaults to current user)
 */
export const createNotification = async (
  title: string,
  message: string,
  type: string,
  userId?: string
) => {
  try {
    // If no userId is provided, get the current user
    if (!userId) {
      const { data } = await supabase.auth.getSession();
      userId = data.session?.user.id;
      
      if (!userId) {
        console.error("No user ID available for notification");
        return;
      }
    }
    
    // Check if a similar notification was created in the last minute
    // to prevent duplicate notifications during batch operations
    const oneMinuteAgo = new Date();
    oneMinuteAgo.setMinutes(oneMinuteAgo.getMinutes() - 1);
    
    const { data: existingNotifications, error: checkError } = await supabase
      .from("notifications")
      .select("id")
      .eq("user_id", userId)
      .eq("title", title)
      .eq("message", message)
      .eq("type", type)
      .gte("created_at", oneMinuteAgo.toISOString());
      
    if (checkError) {
      console.error("Error checking for existing notifications:", checkError);
    } else if (existingNotifications && existingNotifications.length > 0) {
      // Skip creating duplicate notification
      return;
    }
    
    // Create the notification
    const { data, error } = await supabase
      .from("notifications")
      .insert([
        {
          user_id: userId,
          title,
          message,
          type,
          is_read: false
        }
      ]);
      
    if (error) {
      console.error("Error creating notification:", error);
    }
    
    return data;
  } catch (error) {
    console.error("Failed to create notification:", error);
  }
};

/**
 * Creates a deadline notification for all assignees of a card/MFO
 * 
 * @param cardTitle Title of the card/MFO
 * @param dueDate Due date of the card/MFO
 * @param assigneeIds Array of user IDs to notify
 */
export const createDeadlineNotification = async (
  cardTitle: string,
  dueDate: string,
  assigneeIds: string[]
) => {
  try {
    const today = new Date();
    const deadlineDate = new Date(dueDate);
    const daysUntilDue = Math.ceil((deadlineDate.getTime() - today.getTime()) / (1000 * 3600 * 24));
    
    if (daysUntilDue <= 7 && daysUntilDue > 0) {
      const title = "Deadline Approaching";
      const message = `Task "${cardTitle}" is due in ${daysUntilDue} day${daysUntilDue !== 1 ? 's' : ''}`;
      const type = "deadline_approaching";
      
      // Create notification for each assignee
      for (const userId of assigneeIds) {
        await createNotification(title, message, type, userId);
      }
    }
  } catch (error) {
    console.error("Failed to create deadline notifications:", error);
  }
};

/**
 * Checks for upcoming deadlines and sends notifications
 */
export const checkDeadlines = async () => {
  try {
    const today = new Date();
    const sevenDaysLater = new Date(today);
    sevenDaysLater.setDate(today.getDate() + 7);
    
    const formattedToday = today.toISOString().split('T')[0];
    const formattedSevenDaysLater = sevenDaysLater.toISOString().split('T')[0];
    
    // Get cards with approaching deadlines
    const { data: upcomingCards, error: cardsError } = await supabase
      .from('kanban_cards')
      .select(`
        id, 
        title, 
        due_date,
        kanban_card_assignees(
          employee_id
        )
      `)
      .gte('due_date', formattedToday)
      .lte('due_date', formattedSevenDaysLater);
      
    if (cardsError) {
      console.error('Error fetching cards with upcoming deadlines:', cardsError);
      return;
    }
    
    // Process each card
    for (const card of upcomingCards || []) {
      const assigneeIds = card.kanban_card_assignees.map((a: any) => a.employee_id);
      
      if (assigneeIds.length > 0) {
        await createDeadlineNotification(card.title, card.due_date, assigneeIds);
      }
    }
    
    // Also check MFO items
    const { data: upcomingMFOs, error: mfosError } = await supabase
      .from('mfo_items')
      .select(`
        id,
        title,
        due_date,
        mfo_assignees(
          employee_id
        )
      `)
      .gte('due_date', formattedToday)
      .lte('due_date', formattedSevenDaysLater)
      .neq('status', 'completed');
      
    if (mfosError) {
      console.error('Error fetching MFOs with upcoming deadlines:', mfosError);
      return;
    }
    
    // Process each MFO
    for (const mfo of upcomingMFOs || []) {
      const assigneeIds = mfo.mfo_assignees.map((a: any) => a.employee_id);
      
      if (assigneeIds.length > 0) {
        await createDeadlineNotification(mfo.title, mfo.due_date, assigneeIds);
      }
    }
  } catch (error) {
    console.error("Failed to check deadlines:", error);
  }
};
